import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'plerion.theme';

/** True when the user has explicitly chosen a theme via the toggle. */
function hasExplicitPreference(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'dark' || stored === 'light';
}

function resolveInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  // No stored preference — follow OS
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(resolveInitialTheme);
  const isExplicitRef = useRef(hasExplicitPreference());

  // Apply theme to DOM whenever it changes
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const cloudscapeClasses = ['awsui-dark-mode', 'awsui-polaris-dark-mode'];

    if (theme === 'dark') {
      root.classList.add('dark', ...cloudscapeClasses);
      body.classList.add(...cloudscapeClasses);
    } else {
      root.classList.remove('dark', ...cloudscapeClasses);
      body.classList.remove(...cloudscapeClasses);
    }

    // Tells the browser which colour scheme to use for native UI elements
    // (scrollbars, form controls, etc.)
    root.style.colorScheme = theme;
  }, [theme]);

  // Follow OS preference changes while the user hasn't toggled manually
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (!isExplicitRef.current) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const toggleTheme = () => {
    isExplicitRef.current = true;
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      // Only persist when the user makes an explicit choice
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
